import { Button } from '@cloudflare/kumo';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { ContentObject, SiteSnapshot } from '../../shared/content.ts';
import { api, meQuery, queryClient, siteQuery, siteSettingsQuery, sitesQuery, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { SiteAssistantConfigurationEditor } from '../components/site-assistant-settings.tsx';
import { useContentDraft } from '../use-content-draft.ts';
import { Route as parent } from './site.tsx';

export const Route = createRoute({
  getParentRoute: () => parent,
  path: 'site-assistant',
  component: RouteComponent,
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const site = useQuery(siteQuery(siteId, { includePages: false }));
  if (site.isPending) return <Loading />;
  if (site.error) return <ErrorNotice error={site.error} />;
  const snapshot = site.data!;
  const settings = snapshot.objects.find((object) => object.kind === 'Settings');
  return settings?.kind === 'Settings' ? <SiteAssistantForm object={settings} snapshot={snapshot} /> : null;
}

function SiteAssistantForm({
  object,
  snapshot,
}: {
  object: Extract<ContentObject, { kind: 'Settings' }>;
  snapshot: SiteSnapshot;
}) {
  const { draft, setDraft, changed, save } = useContentDraft(snapshot.siteId, object);
  return (
    <section className="max-w-212.5 pt-7 pb-12.5 min-[761px]:pt-[35px] min-[761px]:pb-17.5 grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-5">
        <h2 className="text-[20px] font-semibold tracking-[-0.35px]">Site assistant</h2>
        <Button variant="primary" onClick={() => save.mutate()} disabled={!changed.length || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
      <ErrorNotice error={save.error} />
      <SiteAssistantService siteId={snapshot.siteId} />
      <SiteAssistantConfigurationEditor
        siteId={snapshot.siteId}
        value={draft}
        forms={snapshot.objects.filter((object) => object.kind === 'Form')}
        onChange={setDraft}
      />
    </section>
  );
}

const pendingStatuses = ['Pending', 'Processing', 'AwaitingPaymentMethod', 'AwaitingPayment'];
const servicePaths = {
  Activate: '/api/sites/{siteId}/site-assistant/service',
  Cancel: '/api/sites/{siteId}/site-assistant/service/cancel',
  Restore: '/api/sites/{siteId}/site-assistant/service/restore',
} as const;
const date = (value: string) => new Date(value).toLocaleDateString(undefined, { dateStyle: 'long' });

function SiteAssistantService({ siteId }: { siteId: string }) {
  const me = useQuery(meQuery);
  const sites = useQuery(sitesQuery);
  const metadata = useQuery({
    queryKey: ['site', siteId, 'metadata'],
    queryFn: async () => unwrap(await api.GET('/api/sites/{siteId}', { params: { path: { siteId } } })),
  });
  const site = metadata.data;
  const owner = Boolean(site && me.data?.id === site.ownerUserId);
  const accountId = site?.ownerUserId ?? '';
  const published = useQuery({
    ...siteSettingsQuery(siteId, 'Published'),
    enabled: Boolean(sites.data?.find((site) => site.id === siteId)?.published),
  });
  const billing = useQuery({
    queryKey: ['billing', accountId],
    enabled: owner,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/accounts/{accountUserId}/billing', {
          params: { path: { accountUserId: accountId } },
        }),
      ),
    refetchInterval: (query) =>
      query.state.data?.operations.some(
        (operation) => operation.siteId === siteId && pendingStatuses.includes(operation.status),
      )
        ? 2000
        : false,
  });
  const pending = billing.data?.operations.find(
    (operation) => operation.siteId === siteId && pendingStatuses.includes(operation.status),
  );
  const service = useQuery({
    queryKey: ['site-assistant-service', siteId],
    queryFn: async () =>
      unwrap(await api.GET('/api/sites/{siteId}/site-assistant', { params: { path: { siteId } } })),
    refetchInterval: pending ? 2000 : false,
  });
  useEffect(() => {
    if (billing.dataUpdatedAt)
      void queryClient.invalidateQueries({ queryKey: ['site-assistant-service', siteId] });
  }, [billing.dataUpdatedAt, siteId]);
  const change = useMutation({
    mutationFn: async (kind: keyof typeof servicePaths) =>
      unwrap(
        await api.POST(servicePaths[kind], {
          params: { path: { siteId } },
          body: { idempotencyKey: crypto.randomUUID() },
        }),
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing', accountId] });
      await queryClient.invalidateQueries({ queryKey: ['site-assistant-service', siteId] });
    },
  });
  if (service.isPending || metadata.isPending || me.isPending || sites.isPending || published.isLoading)
    return <Loading />;
  if (service.error || metadata.error || me.error || sites.error || published.error)
    return (
      <ErrorNotice error={service.error ?? metadata.error ?? me.error ?? sites.error ?? published.error} />
    );
  const data = service.data!;
  const base = billing.data?.sites.find((site) => site.id === siteId);
  const eligible =
    data.baseServiceAvailable &&
    !site?.archivedAt &&
    !base?.cancellationAt &&
    ['Trialing', 'Active', 'PastDue'].includes(billing.data?.stripeSubscriptionStatus ?? '');
  const configured = Boolean(
    published.data?.siteAssistantFormId && published.data.locales.some(({ locale }) => locale === 'en'),
  );
  const covered = data.enabled && (!data.cancellationAt || new Date(data.cancellationAt) > new Date());
  return (
    <div className="rounded-xl border border-kumo-line bg-kumo-base p-5 grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold">
            {covered ? (data.cancellationAt ? 'Renewal canceled' : 'Active') : 'Not active'}
          </h3>
          <p className="mt-1 text-sm text-kumo-subtle">
            $8/month for this website, with unlimited conversations.
          </p>
          {covered && data.cancellationAt ? (
            <p className="mt-1 text-sm text-kumo-subtle">Service ends {date(data.cancellationAt)}.</p>
          ) : null}
        </div>
        {owner && !data.billingExempt && covered && !pending ? (
          <Button
            disabled={change.isPending || (Boolean(data.cancellationAt) && !eligible)}
            onClick={() => change.mutate(data.cancellationAt ? 'Restore' : 'Cancel')}
          >
            {data.cancellationAt ? 'Restore renewal' : 'Cancel renewal'}
          </Button>
        ) : null}
      </div>
      <ErrorNotice error={change.error ?? billing.error} />
      {data.billingExempt ? (
        <p className="text-sm text-kumo-subtle">This website is exempt from billing.</p>
      ) : !owner ? (
        <p className="text-sm text-kumo-subtle">Only the website owner can manage this add-on’s billing.</p>
      ) : billing.isPending ? (
        <Loading label="Loading billing…" />
      ) : (
        <>
          {!eligible ? (
            <p className="text-sm text-kumo-subtle">
              Manage the website’s base plan in{' '}
              <Link className="underline" to="/billing" search={{ account: accountId }}>
                Billing
              </Link>{' '}
              to use Site Assistant.
            </p>
          ) : null}
          {!covered && !configured ? (
            <p className="text-sm text-kumo-subtle">
              Select a supported Form below, save changes, and publish the site with the English (en) locale
              before activating.
            </p>
          ) : null}
          {pending ? (
            <div className="grid gap-2 text-sm" role="status">
              <p>
                {pending.service === 'SiteAssistant' && pending.status === 'AwaitingPayment'
                  ? 'Complete payment to activate Site Assistant.'
                  : 'Your billing change is processing…'}
              </p>
              {pending.error ? <p className="text-kumo-subtle">{pending.error}</p> : null}
              <Link className="underline" to="/billing" search={{ account: accountId }}>
                Continue in Billing
              </Link>
            </div>
          ) : !covered ? (
            <>
              {billing.data?.stripeSubscriptionStatus === 'Trialing' && billing.data.trialEndsAt ? (
                <p className="text-sm text-kumo-subtle">
                  Included for the rest of your trial through {date(billing.data.trialEndsAt)}, then $8/month.
                </p>
              ) : billing.data?.periodEndsAt ? (
                <p className="text-sm text-kumo-subtle">
                  Your first charge is prorated through {date(billing.data.periodEndsAt)}. Then $8/month on
                  your existing subscription.
                </p>
              ) : null}
              <Button
                variant="primary"
                className="justify-self-start"
                disabled={change.isPending || !eligible || !configured || !billing.data?.paymentMethod}
                onClick={() => change.mutate('Activate')}
              >
                {change.isPending ? 'Activating…' : 'Activate · $8/month'}
              </Button>
            </>
          ) : null}
          <Link
            className="underline text-sm justify-self-start"
            to="/billing"
            search={{ account: accountId }}
          >
            Manage payment method and invoices
          </Link>
        </>
      )}
    </div>
  );
}
